import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api"; // your BASE_URL must end with /
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";

const SalesStockList = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [transactionType, setTransactionType] = useState("");
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTransactions();
    fetchBalance();
  }, []);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const res = await fetch("https://pbhrm.com/api/salesstocks/transactions");
      const data = await res.json();
      if (data.status === 1) {
        setTransactions(data.transactions);
        setFilteredTransactions(data.transactions);
      } else {
        alert("Failed to fetch sales stock transactions.");
      }
    } catch (err) {
      console.error("Error fetching sales stock transactions:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const res = await fetch(`${BASE_URL}salesstocks/balance?staff_id=1`);
      const data = await res.json();
      if (data.status === 1) {
        setBalance(data.balance);
      } else {
        alert("Failed to fetch balance.");
      }
    } catch (err) {
      console.error("Error fetching balance:", err);
    }
  };

  const filterTransactions = (term, start, end, type) => {
    let filtered = transactions.filter((transaction) => {
      const matchesSearch =
        transaction.staff_name?.toLowerCase().includes(term.toLowerCase()) ||
        transaction.transaction_type
          ?.toLowerCase()
          .includes(term.toLowerCase());

      const transactionDate = new Date(transaction.created_at)
        .toISOString()
        .split("T")[0];

      const matchesDateRange =
        (!start || transactionDate >= start) &&
        (!end || transactionDate <= end);

      const matchesType =
        !type ||
        transaction.transaction_type?.toLowerCase() === type.toLowerCase();

      return matchesSearch && matchesDateRange && matchesType;
    });

    setFilteredTransactions(filtered);
  };

  const handleSearchChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    filterTransactions(term, startDate, endDate, transactionType);
  };

  const handleTransactionTypeChange = (e) => {
    const selectedType = e.target.value;
    setTransactionType(selectedType);
    filterTransactions(searchTerm, startDate, endDate, selectedType);
  };

  const handleDateChange = () => {
    filterTransactions(searchTerm, startDate, endDate, transactionType);
  };

  const handleStockIn = () => navigate("/AddStock");
  const handleStockOut = () => navigate("/StockOut");

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Sales Stock Transactions", 14, 10);

    const tableData = filteredTransactions.map((transaction) => [
      transaction.staff_name || "N/A",
      transaction.amount || "N/A",
      transaction.transaction_type || "N/A",
      transaction.description || "N/A",
      new Date(transaction.created_at).toLocaleString(),
    ]);

    doc.autoTable({
      head: [
        ["Staff Name", "Amount", "Transaction Type", "Description", "Date"],
      ],
      body: tableData,
    });

    doc.save("sales_stock_transactions.pdf");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = ("0" + date.getDate()).slice(-2);
    const month = ("0" + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Sales Stock</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row align-items-center">
                  <div className="col-md-4">
                    <h5>
                      Total Balance:{" "}
                      {balance !== null ? `Rs. ${balance}` : "Loading..."}
                    </h5>
                  </div>
                  <div className="col-md-2 text-end">
                    <button className="btn btn-primary" onClick={handleStockIn}>
                      Stock In
                    </button>
                  </div>
                  <div className="col-md-2 text-end">
                    <button
                      className="btn btn-danger ms-2"
                      onClick={handleStockOut}
                    >
                      Stock Out
                    </button>
                  </div>
                  <div className="col-md-4 text-end">
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={handleExportPDF}
                      disabled={filteredTransactions.length === 0}
                    >
                      Export to PDF
                    </button>
                  </div>
                </div>
              </div>

              <div className="card-body">
                {/* Filters */}
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label htmlFor="searchTerm" className="form-label">
                      Search by Name
                    </label>
                    <input
                      type="text"
                      id="searchTerm"
                      className="form-control"
                      placeholder="Search by staff name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="col-md-3">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-3">
                    <label htmlFor="transactionType" className="form-label">
                      Transaction Type
                    </label>
                    <select
                      id="transactionType"
                      className="form-control"
                      value={transactionType}
                      onChange={handleTransactionTypeChange}
                    >
                      <option value="">All</option>
                      <option value="outgoing">Outgoing</option>
                      <option value="incoming">Incoming</option>
                    </select>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4 mt-4">
                    <button
                      className="btn btn-secondary"
                      onClick={handleDateChange}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>

                {/* Table */}
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center py-3">
                      Loading transactions…
                    </div>
                  ) : (
                    <table className="table table-lg">
                      <thead>
                        <tr>
                          <th>STAFF NAME</th>
                          <th>AMOUNT</th>
                          <th>TRANSACTION TYPE</th>
                          <th>DESCRIPTION</th>
                          <th>DATE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.length > 0 ? (
                          filteredTransactions.map((transaction) => (
                            <tr key={transaction.id}>
                              <td>{transaction.staff_name || "N/A"}</td>
                              <td>{transaction.amount || "N/A"}</td>
                              <td>{transaction.transaction_type || "N/A"}</td>
                              <td>{transaction.description || "N/A"}</td>
                              <td>{formatDate(transaction.created_at)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No transactions found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SalesStockList;
